export function sheetRowHasVenueWineId(sheetRow) {
  return (
    sheetRow.pqVenueWineId != null &&
    sheetRow.pqVenueWineId !== "" &&
    sheetRow.pqVenueWineId !== "n/a"
  );
}

export function sheetRowHasChangedVenueWineId(sheetRow) {
  return (
    sheetRow.originalPqVenueWineId != null &&
    sheetRow.pqVenueWineId !== sheetRow.originalPqVenueWineId
  );
}

export function sheetRowHasEmptyUpdaterStatus(sheetRow) {
  return sheetRow.updaterStatus == null || sheetRow.updaterStatus === "";
}

/**
 * Safely check whether an updater status value contains a given text.
 * The updater status can be null/undefined (e.g. after a match was changed
 * in the receipt matcher) or a non-string value, so it must not be assumed
 * to be a string.
 *
 * @param updaterStatus The "Updater Status" column value of the row
 * @param text The text to look for
 * @returns {boolean}
 */
export function updaterStatusIncludes(updaterStatus, text) {
  if (updaterStatus == null) return false;
  return String(updaterStatus).includes(text);
}

export function isIgnoredUpdaterStatus(updaterStatus) {
  return updaterStatusIncludes(updaterStatus, UpdaterStatusSheetText.Ignored);
}

export function sheetRowIsIgnored(sheetRow) {
  return isIgnoredUpdaterStatus(sheetRow.updaterStatus);
}

export function sheetRowHasFinishedUpdaterStatus(sheetRow) {
  return isFinishedUpdaterStatus(sheetRow.updaterStatus);
}

export function isFinishedUpdaterStatus(updaterStatus) {
  const isCreated =
    updaterStatusIncludes(updaterStatus, UpdaterStatusSheetText.VenueWineCreated) ||
    updaterStatusIncludes(updaterStatus, UpdaterStatusSheetText.WineAndVenueWineCreated);
  const isUpdated = updaterStatusIncludes(
    updaterStatus,
    UpdaterStatusSheetText.UpdatedValues,
  );
  const isIgnored = updaterStatusIncludes(
    updaterStatus,
    UpdaterStatusSheetText.Ignored,
  );

  return isCreated || isUpdated || isIgnored;
}

export function canImportSheetRow(sheetRow) {
  return (
    sheetRowHasVenueWineId(sheetRow) &&
    !sheetRowHasChangedVenueWineId(sheetRow) &&
    (sheetRowHasEmptyUpdaterStatus(sheetRow) ||
      !sheetRowHasFinishedUpdaterStatus(sheetRow))
  );
}

export function canSaveSheetRow(sheetRow) {
  return (
    sheetRowHasVenueWineId(sheetRow) && 
    sheetRowHasChangedVenueWineId(sheetRow)
  );
}

export function canCreateProductFromSheetRow(sheetRow) {
  return (
    !sheetRowHasVenueWineId(sheetRow) &&
    sheetRowHasEmptyUpdaterStatus(sheetRow)
  );
}

export function canIgnoreSheetRow(sheetRow) {
  return !isFinishedUpdaterStatus(sheetRow.updaterStatus);
}

export function filterSheetRowsForImport(sheetRows) {
  return sheetRows.filter((row) => canImportSheetRow(row));
}

export function filterSheetRowsForSave(sheetRows) {
  return sheetRows.filter((row) => sheetRowHasChangedVenueWineId(row));
}

/**
 * These are the possible updater status values that are directly
 * set in the google sheet row by the updater.
 */
export const UpdaterStatusSheetText = {
  VenueWineCreated: "VenueWine mit WineID wurde erstellt",
  WineAndVenueWineCreated: "Wein und VenueWine wurden erstellt",
  UpdatedValues: "Werte wurden angepasst:",
  LowScore: "Low Score - Vorschlag prüfen",
  BetterWineFound: "Besseren Wein gefunden",
  NoMatch: "Keine Übereinstimmung - Prüfen",
  Ignored: "Ignorieren",
};

/**
 * These are the user friendly updater status badge texts
 * that are displayed in the UI
 */
export const UpdaterStatusBadgeText = {
  ProductNotFound: "Produkt nicht auffindbar",
  ProducerNotFound: "Hersteller nicht gefunden",
  ProductMatchNeedsCheck: "Zuordnung prüfen",
  ProductFound: "Produkt gefunden",
  ProductCreated: "Produkt neu angelegt",
  AmountUpdated: "Bestand Flaschen",
  AmountGlassesUpdated: "Bestand Gläser",
  SellingPriceUpdated: "EK Preis",
  ProductIgnored: "Position wird ignoriert",
};

export const UpdaterStatusBadgeColor = {
  Red: "ROT",
  Yellow: "GELB",
  Green: "GRÜN",
};

export const UPDATER_STATUS_BADGE_CATEGORY = {
  [UpdaterStatusBadgeText.ProductNotFound]: UpdaterStatusBadgeColor.Red,
  [UpdaterStatusBadgeText.ProducerNotFound]: UpdaterStatusBadgeColor.Red,
  [UpdaterStatusBadgeText.ProductMatchNeedsCheck]:
    UpdaterStatusBadgeColor.Yellow,
  [UpdaterStatusBadgeText.ProductFound]: UpdaterStatusBadgeColor.Green,
  [UpdaterStatusBadgeText.ProductCreated]: UpdaterStatusBadgeColor.Green,
  [UpdaterStatusBadgeText.AmountUpdated]: UpdaterStatusBadgeColor.Green,
  [UpdaterStatusBadgeText.AmountGlassesUpdated]: UpdaterStatusBadgeColor.Green,
  [UpdaterStatusBadgeText.SellingPriceUpdated]: UpdaterStatusBadgeColor.Green,
  [UpdaterStatusBadgeText.ProductIgnored]: UpdaterStatusBadgeColor.Green,
};

/**
 * Determine the user friendly status text based on the updater status of a row.
 *
 * For amount updates, each returned message includes the actual before/after
 * amounts, e.g. "Bestand Flaschen: von 24 auf 60 erhöht".
 *
 * @param updaterStatus The "Updater Status" column value of the row
 * @returns {Array<{text: string, colorClass: string}>} List of badge descriptions
 */
export function buildUpdaterStatusBadges(updaterStatus) {
  if (!updaterStatus) {
    return [];
  }

  const badges = [];

  if (
    updaterStatus.includes(UpdaterStatusSheetText.VenueWineCreated) ||
    updaterStatus.includes(UpdaterStatusSheetText.WineAndVenueWineCreated)
  ) {
    badges.push(createStatusBadge(UpdaterStatusBadgeText.ProductCreated));
  }

  if (updaterStatus.includes(UpdaterStatusSheetText.UpdatedValues)) {
    badges.push(...buildUpdatedValuesBadges(updaterStatus));
  }

  if (
    updaterStatus.includes(UpdaterStatusSheetText.LowScore) ||
    updaterStatus.includes(UpdaterStatusSheetText.BetterWineFound)
  ) {
    badges.push(
      createStatusBadge(UpdaterStatusBadgeText.ProductMatchNeedsCheck),
    );
  }

  if (updaterStatus.includes(UpdaterStatusSheetText.NoMatch)) {
    badges.push(createStatusBadge(UpdaterStatusBadgeText.ProductNotFound));
  }

  if (updaterStatus.includes(UpdaterStatusSheetText.Ignored)) {
    badges.push(createStatusBadge(UpdaterStatusBadgeText.ProductIgnored));
  }

  return badges;
}

export function createStatusBadge(reason) {
  const color =
    UPDATER_STATUS_BADGE_CATEGORY[reason] || UpdaterStatusBadgeColor.Yellow;
  return {
    text: reason,
    colorClass: badgeColorClass(color),
  };
}

/**
 * Build a badge list description for one or more value updates, e.g.
 * "Bestand Flaschen: von 24 auf 48 erhöht" or
 * "Bestand Flaschen: von 2 auf 1 reduziert, EK Preis: von 230€ auf 190€".
 *
 * @param {string} updaterStatus The "Updater Status" column value of the row
 * @returns {Array<{text: string, colorClass: string}>}
 */
export function buildUpdatedValuesBadges(updaterStatus) {
  if (updaterStatus == null) {
    return [];
  }

  const updates = String(updaterStatus)
    .replace(UpdaterStatusSheetText.UpdatedValues, "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  return updates.map((update) => buildSingleUpdateReason(update));
}

/**
 * Build the reason message for a single field update, e.g. "amountFloat von 2 auf 1".
 *
 * @param {string} update The single field update text
 * @returns {string} The formatted reason message for this update
 */
export function buildSingleUpdateReason(update) {
  const match = update.match(
    /^(\w+) von (-?\d+(?:\.\d+)?|null) auf (-?\d+(?:\.\d+)?|null)$/,
  );
  if (!match) {
    return createStatusBadgeFromText(update);
  }

  const field = match[1];
  const before = match[2] === "null" ? "0" : match[2];
  const after = match[3] === "null" ? "0" : match[3];

  const changeWord = compareValues(before, after) < 0 ? "erhöht" : "reduziert";

  let reason;
  if (field === "amountFloat") {
    reason = `${UpdaterStatusBadgeText.AmountUpdated}: von ${before} auf ${after} ${changeWord}`;
  } else if (field === "amountGlasses") {
    reason = `${UpdaterStatusBadgeText.AmountGlassesUpdated}: von ${before} auf ${after} ${changeWord}`;
  } else if (field === "sellingPrice") {
    reason = `${UpdaterStatusBadgeText.SellingPriceUpdated}: von ${before}€ auf ${after}€ ${changeWord}`;
  } else {
    reason = `${field}: von ${before} auf ${after} ${changeWord}`;
  }

  return createStatusBadgeFromText(reason);
}

/**
 * Create a badge object for a full reason message (not mapped to a single
 * predefined reason). Update messages are treated as successful (green) changes.
 *
 * @param {string} text
 * @returns {{text: string, colorClass: string}}
 */
export function createStatusBadgeFromText(text) {
  return {
    text,
    colorClass: badgeColorClass(UpdaterStatusBadgeColor.Green),
  };
}

/**
 * Compare two values that may be numeric strings or "null".
 * Returns a negative number if before < after, 0 if equal, positive if before > after.
 *
 * @param {string} before
 * @param {string} after
 * @returns {number}
 */
export function compareValues(before, after) {
  const beforeNum =
    before === "null" ? Number.NEGATIVE_INFINITY : parseFloat(before);
  const afterNum =
    after === "null" ? Number.NEGATIVE_INFINITY : parseFloat(after);
  return beforeNum - afterNum;
}

/**
 * Maps an UpdaterStatusBadgeColor to a CSS class used for badge coloring.
 *
 * @param {string} color The UpdaterStatusBadgeColor constant ('ROT' | 'GELB' | 'GRÜN')
 * @returns {string}
 */
export function badgeColorClass(color) {
  switch (color) {
    case UpdaterStatusBadgeColor.Red:
      return "badge-red";
    case UpdaterStatusBadgeColor.Green:
      return "badge-green";
    case UpdaterStatusBadgeColor.Yellow:
    default:
      return "badge-yellow";
  }
}
